/**
 * Which waiting-room customers are NEW arrivals the doctor should be alerted
 * about (#48).
 *
 * The doctor dashboard listens to `waitingRooms/{coachId-date}/customers`
 * (PresenceService.listenToWaitingRoom). That snapshot fires for every
 * heartbeat (every 5s per customer), so "a doc in the snapshot I haven't
 * alerted for" must be keyed on something heartbeats never change:
 * `customerId + joinTime`. joinTime is only written by joinWaitingRoom (a full
 * setDoc), while sendHeartbeat merges `lastHeartbeat` alone — so a heartbeat
 * re-write keeps the key, and a heartbeat that lands after the doc was deleted
 * creates a doc WITHOUT joinTime, which is never an arrival. A real re-join
 * writes a fresh joinTime and therefore alerts again.
 */

export interface ArrivalCandidate {
  customerId: string;
  joinTime?: unknown;
}

/** Milliseconds of a Firestore Timestamp-ish value, or null when absent/unreadable. */
export function joinTimeMillis(joinTime: unknown): number | null {
  if (joinTime == null) return null;
  if (typeof joinTime === 'number') return Number.isFinite(joinTime) ? joinTime : null;
  if (typeof joinTime === 'object') {
    const t = joinTime as { toMillis?: () => number; seconds?: unknown; nanoseconds?: unknown };
    if (typeof t.toMillis === 'function') {
      const ms = t.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof t.seconds === 'number') {
      const nanos = typeof t.nanoseconds === 'number' ? t.nanoseconds : 0;
      return t.seconds * 1000 + Math.floor(nanos / 1e6);
    }
  }
  return null;
}

/** Stable identity of one visit to the waiting room; null = not a real join. */
export function arrivalKey(customer: ArrivalCandidate): string | null {
  if (!customer.customerId) return null;
  const ms = joinTimeMillis(customer.joinTime);
  return ms == null ? null : `${customer.customerId}@${ms}`;
}

/**
 * @param seen keys already accounted for, or null for the listener's first
 *   snapshot — everything present when the dashboard loads is the baseline,
 *   never an alert.
 * @returns the arrivals to alert for (in snapshot order) and the updated seen
 *   set (a new Set; the input is not mutated).
 */
export function detectNewArrivals<T extends ArrivalCandidate>(
  seen: ReadonlySet<string> | null,
  customers: readonly T[]
): { arrivals: T[]; seen: Set<string> } {
  const next = new Set(seen ?? []);
  const arrivals: T[] = [];
  for (const customer of customers) {
    const key = arrivalKey(customer);
    if (key == null || next.has(key)) continue;
    next.add(key);
    if (seen !== null) arrivals.push(customer);
  }
  return { arrivals, seen: next };
}
